// Insurance — shared policy status derivation. The lapse worker flips
// InsurancePolicy.Status "active" → "expired"; legacy rows/fixtures may
// carry other casings ("Active"/"Expired"/"Lapsed"), so compare
// case-insensitively and treat a past expiration date as lapsed too.

import { daysUntil } from "../common/colorBand";
import type { InsurancePolicyDto } from "@/api/portfolioAdmin/types";

/**
 * ⚠️ `tz` (the VIEWER's zone) makes this UNSAFE to pass bare to `.filter`.
 * `Array.prototype.filter` calls its predicate with `(element, index, array)`,
 * so `policies.filter(isLapsed)` would bind the row INDEX to `tz`. That is a
 * type error rather than a silent defect — which is why the parameter goes
 * here and not behind an options object — but every such call site has to
 * become `policies.filter((p) => isLapsed(p, tz))`.
 *
 * Omitting `tz` falls back to Phoenix, so a caller with no zone to offer is
 * byte-identical to before.
 */
export function isLapsed(
  policy: InsurancePolicyDto,
): boolean {
  const status = policy.status.toLowerCase();
  return (
    status === "expired" ||
    status === "lapsed" ||
    (daysUntil(policy.expirationDate) ?? 1) < 0
  );
}

/**
 * Active policy expiring within `windowDays` (exclusive of lapsed).
 *
 * `tz` sits at position 3 because `windowDays` is REQUIRED — the rule is that
 * a new optional zone must never be able to bind to an argument a caller
 * already passes, and there is no trailing `now` here for it to displace.
 */
export function isExpiringWithin(
  policy: InsurancePolicyDto,
  windowDays: number,
): boolean {
  if (isLapsed(policy)) return false;
  const days = daysUntil(policy.expirationDate);
  return days !== null && days <= windowDays;
}
